import { hiddenCategories } from 'components/marketingSite/blog/utils/blogUtils';
import fetch, { Headers } from 'node-fetch';
import { config } from 'utils/config';
import type {
  WP_REST_API_Categories,
  WP_REST_API_Category,
  WP_REST_API_Post,
  WP_REST_API_Posts
} from 'wp-types';

const BASE_URL = `${config.wpUrl}/wp-json/wp/v2`;
const ENABLE_LOG_REQUESTS = true;

const getTeam = () => {
  return fetchWP(`${config.wpUrl}/wp-json/custom/v1/team`).then((response) =>
    response.json()
  );
};

const getPosts = ({
  page,
  perPage = 9,
  category,
  exclude,
  featured = 'exclude',
  locale
}: {
  page?: number;
  perPage?: number;
  category?: string;
  exclude?: string;
  featured?: 'include' | 'exclude' | 'ignore';
  locale?: string;
}): Promise<void | {
  data: WP_REST_API_Posts;
  headers: Headers;
}> => {
  const params = new URLSearchParams();

  if (page) {
    params.set('page', page.toString());
  }

  if (perPage) {
    params.set('per_page', perPage.toString());
  }

  if (category) {
    params.set('categories', category);
  }

  if (locale) {
    params.set('lang', locale.toLowerCase());
  }
  if (featured === 'include') {
    params.set('sticky', 'true');
  } else if (featured === 'exclude') {
    params.set('sticky', 'false');
  }
  if (exclude) {
    params.set('exclude', exclude);
  }

  params.set('orderby', 'date');
  params.set('order', 'desc');
  params.set('_embed', 'true');

  return fetchWP(`${BASE_URL}/posts?${params}`).then(async (response) => ({
    data: await response.json(),
    headers: response.headers
  }));
};

const getPost = async (
  slug: string,
  locale: string
): Promise<WP_REST_API_Post> => {
  const params = new URLSearchParams();

  params.set('slug', slug);
  params.set('_embed', 'true');
  params.set('lang', locale);

  const posts = await fetchWP(`${BASE_URL}/posts?${params}`).then((response) =>
    response.json()
  );

  return posts[0];
};

type WP_REST_API_Tag = {
  id: number;
  name: string;
  slug: string;
  description: string;
  count: number;
};

const getTagsFromPosts = async ({
  posts,
  locale = 'en'
}: {
  posts: WP_REST_API_Post[];
  locale: string;
}): Promise<WP_REST_API_Tag[]> => {
  if (!posts || posts.length === 0) {
    return [];
  }

  const allTagIds = new Set<number>();
  posts.forEach((post) => {
    if (post.tags) {
      post.tags.forEach((tagId) => allTagIds.add(tagId));
    }
  });

  if (allTagIds.size === 0) {
    return [];
  }

  try {
    const tagIds = Array.from(allTagIds).join(',');
    const tags: WP_REST_API_Tag[] = await fetchWP(
      `${BASE_URL}/tags?lang=${locale}&include=${tagIds}`
    ).then((response) => response.json());
    return tags;
  } catch (error) {
    console.error('Failed to fetch tags for posts:', error);
    return [];
  }
};

const getAllTags = async (
  locale: string = 'en'
): Promise<WP_REST_API_Tag[]> => {
  const perPage = 100;

  const params = new URLSearchParams({
    lang: locale,
    per_page: perPage.toString()
  });

  const response = await fetchWP(`${BASE_URL}/tags?${params}`);

  if (!response.ok) {
    throw new Error(`WP Tags fetch failed: ${response.status}`);
  }

  const tagsPage: WP_REST_API_Tag[] = await response.json();

  return tagsPage;
};

const getCategoriesInfo = async (
  categories: number[] = []
): Promise<WP_REST_API_Categories> => {
  const params = new URLSearchParams();

  if (categories.length > 0) {
    params.set('include', categories.join(','));
    params.set('_embed', 'true');
  }

  return fetchWP(`${BASE_URL}/categories?${params}`).then((response) =>
    response.json()
  );
};

const getCategories = async (
  locale?: string
): Promise<
  (WP_REST_API_Category & {
    language_slugs: {
      en: string;
      es: string;
    };
    category_lang: string;
  })[]
> => {
  const params = new URLSearchParams();

  params.set('lang', locale || '');

  return fetchWP(`${BASE_URL}/categories?${params}`).then((response) =>
    response.json()
  );
};

const getAllCategories = async (
  locale?: string
): Promise<WP_REST_API_Category[]> => {
  const perPage = 100;

  const params = new URLSearchParams();
  params.set('lang', locale || '');
  params.set('per_page', perPage.toString());

  const response = await fetchWP(`${BASE_URL}/categories?${params}`);
  const categories: WP_REST_API_Category[] = await response.json();

  return categories;
};

const getSlugs = async (locale: string): Promise<string[]> => {
  const res = await getPosts({
    locale
  });

  const posts = [];

  if (res) {
    posts.push(res.data);
  }

  return posts.flat().map((p) => p.slug);
};

const getPostsByCategorySlug = async ({
  page,
  perPage,
  slug,
  locale
}: {
  slug: string;
  page?: number;
  perPage?: number;
  category?: string;
  locale?: string;
}): Promise<void | {
  data: WP_REST_API_Posts;
  headers: Headers;
}> => {
  const categories = await WPService.getAllCategories(locale);
  const categoryId = categories.filter((c) => c.slug === slug).shift()?.id;

  return getPosts({
    category: categoryId?.toString(),
    page,
    perPage,
    locale
  });
};

const fetchWP = async (url: string) => {
  if (ENABLE_LOG_REQUESTS) console.log(`[WPService] 💬 fetching ${url}`);
  return fetch(url);
};

export const getRelatedPosts = async (
  currentPost: WP_REST_API_Post,
  locale: string
): Promise<WP_REST_API_Post[]> => {
  const postCategories = (currentPost.categories ?? []).filter(
    (catId, _, arr) =>
      !hiddenCategories.includes(
        Array.isArray(currentPost._embedded?.['wp:term']?.[0])
          ? currentPost._embedded?.['wp:term']?.[0].find(
              (c: any) => c.id === catId
            )?.slug
          : undefined
      )
  );

  if (!postCategories.length) return [];

  const res = await WPService.getPosts({
    perPage: 3,
    category: postCategories.join(','),
    exclude: currentPost.id.toString(),
    locale
  });

  const related = (res?.data ?? [])
    .filter((post) => post.id !== currentPost.id)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return related;
};

const WPService = {
  getPosts,
  getPost,
  getSlugs,
  getAllTags,
  getCategoriesInfo,
  getCategories,
  getAllCategories,
  getPostsByCategorySlug,
  getRelatedPosts,
  getTagsFromPosts,
  getTeam
};

export default WPService;
